import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addBet, removeBet } from "../../../store/betslipold-slice";
import { toastService } from "../../notificationPage/ToastContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faClock,
  faExclamationTriangle,
  faArrowUp,
  faArrowDown,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

const MatchRow = ({ match, getOddsGroup, mobileDisplayOption }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bets = useSelector((state) => state.betSlip.bets);

  // Find any existing bet for this match
  const existingBet = bets.find((bet) => bet.match.id === match.gameId);

  const handleMatchClick = () => {
    // Navigate to match profile using match ID
    navigate(`/match/${match.gameId}`);
  };

  // Get trading status info for display
  const getTradingStatusInfo = (odd) => {
    const status = odd.tradingStatus || "Open";

    switch (status) {
      case "Closed":
        return {
          icon: faBan,
          text: "Closed",
          color: "text-red-500",
          bgColor: "bg-red-100 dark:bg-red-900/30",
          canSelect: false,
        };
      case "Suspended":
        return {
          icon: faClock,
          text: "Suspended",
          color: "text-yellow-500",
          bgColor: "bg-yellow-100 dark:bg-yellow-900/30",
          canSelect: true, // Allow selection but with warning
        };
      case "Open":
      default:
        return {
          canSelect: true,
        };
    }
  };

  // Check if odds have changed
  const hasOddsChanged = (odd) => {
    return odd.originalValue && odd.originalValue !== odd.value;
  };

  // Get odds change indicator
  const getOddsChangeIndicator = (odd) => {
    if (!hasOddsChanged(odd)) return null;

    const originalValue = parseFloat(odd.originalValue);
    const currentValue = parseFloat(odd.value);
    const isIncrease = currentValue > originalValue;

    return {
      isIncrease,
      icon: isIncrease ? faArrowUp : faArrowDown,
      color: isIncrease ? "text-green-500" : "text-red-500",
      percentage: (
        ((currentValue - originalValue) / originalValue) *
        100
      ).toFixed(1),
    };
  };

  // Enhanced odd selection with proper selectionId handling
  const handleOddSelect = (odd) => {
    const statusInfo = getTradingStatusInfo(odd);

    // Prevent selection if market is closed
    if (!statusInfo.canSelect) {
      toastService.error(
        `Cannot select - Market is ${odd.tradingStatus || "closed"}`
      );
      return;
    }

    // Show warning for suspended markets but allow selection
    if (odd.tradingStatus === "Suspended") {
      toastService.warning(
        `Market is suspended - Selection added but may not be placeable`
      );
    }

    // Create enhanced match data
    const matchData = {
      id: match.gameId,
      gameName: `${match.team1} V ${match.team2}`, // Updated to match expected field name
      name: `${match.team1} V ${match.team2}`,
      date: match.eventDate,
      startTime: match.eventDate,
      team1: match.team1,
      team2: match.team2,
    };

    // Create market data
    const market = {
      marketName: odd.market || "Match Result",
      tradingStatus: odd.tradingStatus || "Open",
      gameId: match.gameId,
      marketId: `${match.gameId}_${odd.market || "Match Result"}`,
    };

    // Enhanced odd data with all required fields
    const enhancedOdd = {
      ...odd,
      label: odd.label,
      value: odd.value,
      originalValue: odd.originalValue || odd.value,
      currentValue: odd.value,
      tradingStatus: odd.tradingStatus || "Open",
      oddId: odd.oddId || `${match.gameId}_${odd.market}_${odd.label}`,
      gameId: match.gameId,
      // Generate selectionId if not provided - this should ideally come from your API
      selectionId:
        odd.selectionId ||
        `${match.gameId}_${odd.market || "Match Result"}_${odd.label}`,
    };

    console.log("✅ Clicked match ID:", match.gameId);
    console.log("✅ Selection ID:", enhancedOdd.selectionId);

    // Check if this specific selection is already in betslip
    const betExists = isOddSelected(enhancedOdd, match.gameId);
    const hasAnyBetForMatch = bets.some((bet) => bet.match.id === match.gameId);

    if (betExists) {
      // Remove the bet if it's already selected using selectionId
      const existingBetToRemove = bets.find(
        (bet) =>
          bet.selectionId === enhancedOdd.selectionId ||
          (bet.match.id === match.gameId &&
            bet.odd.label === enhancedOdd.label &&
            bet.market?.marketName === market.marketName)
      );

      if (existingBetToRemove) {
        dispatch(
          removeBet({
            selectionId: existingBetToRemove.selectionId,
            match: matchData,
            odd: enhancedOdd,
            market,
          })
        );
        toastService.warning(`Removed: ${enhancedOdd.label}`);
      }
    } else if (hasAnyBetForMatch) {
      // Remove existing bet for this match before adding new one
      dispatch(
        removeBet({
          selectionId: existingBet.selectionId,
          match: existingBet.match,
          odd: existingBet.odd,
          market: existingBet.market,
        })
      );

      // Add the new bet with enhanced data
      dispatch(
        addBet({
          match: matchData,
          odd: enhancedOdd,
          market,
          selectionId: enhancedOdd.selectionId,
        })
      );
      toastService.info(`Changed selection to: ${enhancedOdd.label}`);
    } else {
      // Add new bet when no bet exists for this match
      dispatch(
        addBet({
          match: matchData,
          odd: enhancedOdd,
          market,
          selectionId: enhancedOdd.selectionId,
        })
      );
      toastService.success(
        `Added: ${enhancedOdd.label} @ ${enhancedOdd.value}`
      );
    }
  };

  // Enhanced selection check using selectionId when available
  const isOddSelected = useCallback(
    (odd, gameId) => {
      // First try to match by selectionId (most reliable)
      if (odd.selectionId) {
        return bets.some((bet) => bet.selectionId === odd.selectionId);
      }

      // Fallback to the original method
      return bets.some((bet) => {
        return (
          bet.match.id === gameId &&
          bet.odd.label === odd.label &&
          bet.market?.marketName === odd.market
        );
      });
    },
    [bets]
  );

  // Check if odd should be disabled
  const isOddDisabled = (odd) => {
    const statusInfo = getTradingStatusInfo(odd);
    return !statusInfo.canSelect;
  };

  // Enhanced styling for odds with better selected state design
  const getOddStyling = (odd, isSelected) => {
    const statusInfo = getTradingStatusInfo(odd);
    const disabled = isOddDisabled(odd);

    let baseClasses =
      "odd-block cursor-pointer rounded-[6px] w-full h-16 max-sm:h-12 flex flex-col items-center justify-center gap-[4px] border transition-all duration-300";

    if (disabled) {
      // Disabled state for closed markets
      return `${baseClasses} opacity-50 cursor-not-allowed bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 scale-95`;
    }

    if (odd.tradingStatus === "Suspended") {
      // Suspended state - yellowish tint
      baseClasses += ` ${statusInfo.bgColor} border-yellow-400`;
    }

    if (isSelected) {
      // Enhanced selected state with better visual feedback
      return `${baseClasses} 
        dark:bg-[#E2E2E2] bg-yellow text-black border-yellow`;
    }

    // Default state with hover effects
    const hoverClasses = statusInfo.canSelect
      ? `hover:bg-yellow-50 dark:hover:bg-yellow-900/20 
         hover:border-yellow-300 dark:hover:border-yellow-600 
         hover:shadow-md hover:scale-102 
         hover:ring-1 hover:ring-yellow-300 hover:ring-opacity-50`
      : "";

    // Dimmed state for non-selected odds when another odd is selected for this match
    // const dimmedState =
    //   existingBet && !isSelected ? "opacity-60 scale-98" : "opacity-100";

    return `${baseClasses} 
      bg-[#F3F4F6] dark:bg-[#123968] 
      border-[#E5E7EB] dark:border-doubleextralightwhite 
      ${hoverClasses}
      hover:z-0`;
  };

  // Get label styling based on selection state
  const getLabelStyling = (odd, isSelected, disabled) => {
    let baseClasses =
      "text-[12px] max-sm:text-[10px] font-normal transition-colors duration-300";

    if (disabled) {
      return `${baseClasses} text-gray-400`;
    }

    if (isSelected) {
      return `${baseClasses} text-black font-semibold`;
    }

    return `${baseClasses} text-[#A3A3A3] dark:text-extralightwhite`;
  };

  // Get value styling based on selection state
  const getValueStyling = (odd, isSelected, disabled) => {
    let baseClasses =
      "text-sm s18:text-base max-sm:text-xs font-medium transition-colors duration-300";

    if (disabled) {
      return `${baseClasses} text-gray-400`;
    }

    if (isSelected) {
      return `${baseClasses} text-black font-bold`;
    }

    return `${baseClasses} text-mutedblue dark:text-white`;
  };
  const getDefaultMarketName = (columnIndex) => {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    if (isMobile) {
      return mobileDisplayOption === "gg-ng"
        ? "Both Teams To Score"
        : mobileDisplayOption === "over-under"
        ? "Total Goals"
        : mobileDisplayOption === "double-chance"
        ? "Double Chance"
        : "Match Result";
    }

    const desktopMarkets = ["Match Result", "Double Chance"];
    if (mobileDisplayOption === "gg-ng") {
      desktopMarkets.push("Both Teams To Score");
    } else {
      desktopMarkets.push("Total Goals");
    }

    return desktopMarkets[columnIndex] || "Match Result";
  };

  // Add safety check for match.odds
  const safeOdds = Array.isArray(match.odds) ? match.odds : [];

  return (
    <div className="flex justify-between items-center max-sm:p-[15px_20px] py-2 s16:py-3 px-5 s18:p-[15px_23px]">
      {/* Teams - Fixed width container */}
      <div
        onClick={handleMatchClick}
        className="cursor-pointer w-[200px] max-lg:w-[180px] max-md:w-[150px] max-sm:w-[120px] flex-shrink-0 mr-4"
      >
        <p className="text-mutedblue dark:text-white max-sm:text-xs text-sm s18:text-base font-normal leading-[143%] md:leading-tight s18:leading-[143%] truncate">
          {match.team1}
        </p>
        <p className="text-mutedblue dark:text-white max-sm:text-xs text-sm s18:text-base font-normal leading-[143%] md:leading-tight s18:leading-[143%] truncate">
          {match.team2}
        </p>
        <p className="flex items-center gap-[5px] mt-[5px] max-sm:flex-wrap max-sm:gap-[3px] max-sm:mt-1">
          {Array.isArray(match.details) &&
            match.details.map((detail, detailIndex) => (
              <span
                key={detailIndex}
                className={`max-sm:text-[9px] text-[12px] leading-[133%] ${
                  detail.bold ? "font-bold" : "font-normal"
                } ${
                  detail.colorRed
                    ? "text-[#F87171] dark:text-[#F87171]"
                    : "text-[#737373] dark:text-extralightwhite"
                }`}
              >
                {detail.text}
              </span>
            ))}
        </p>
      </div>

      {/* Odds */}
      <div className="scrollable overflow-auto flex-grow">
        <div className="min-w-[300px] max-sm:min-w-[160px] grid grid-cols-12 gap-[12px] xl:gap-[18px]">
          {(() => {
            const isMobile = window.matchMedia("(max-width: 767px)").matches;
            const columnCount = isMobile ? 1 : 3;

            return Array.from({ length: columnCount }, (_, columnIndex) => {
              const group = getOddsGroup(match, columnIndex);

              return (
                <div
                  key={columnIndex}
                  className={isMobile ? "col-span-12" : "col-span-4"}
                >
                  {group ? (
                    <div className="flex gap-[4px]">
                      {Array.isArray(group) &&
                        group.map((odd, oddIndex) => {
                          // Add market information to odd if not present
                          const oddWithMarket = {
                            ...odd,
                            market:
                              odd.market ||
                              odd.marketName ||
                              getDefaultMarketName(columnIndex),
                            selectionId:
                              odd.selectionId ||
                              `${match.gameId}_${
                                odd.market || getDefaultMarketName(columnIndex)
                              }_${odd.label}`,
                          };

                          const isSelected = isOddSelected(
                            oddWithMarket,
                            match.gameId
                          );
                          const statusInfo =
                            getTradingStatusInfo(oddWithMarket);
                          const oddsChangeInfo =
                            getOddsChangeIndicator(oddWithMarket);
                          const disabled = isOddDisabled(oddWithMarket);

                          return (
                            <div key={oddIndex} className="w-3/4 sm:w-1/2">
                              <div
                                className={getOddStyling(
                                  oddWithMarket,
                                  isSelected
                                )}
                                onClick={() =>
                                  !disabled && handleOddSelect(oddWithMarket)
                                }
                              >
                                {/* Trading Status Indicator */}
                                {statusInfo.icon && (
                                  <div className="absolute top-1 left-1">
                                    <FontAwesomeIcon
                                      icon={statusInfo.icon}
                                      className={`${statusInfo.color} text-xs`}
                                      title={statusInfo.text}
                                    />
                                  </div>
                                )}

                                {/* Odds Change Indicator */}
                                {oddsChangeInfo && !statusInfo.icon && (
                                  <div className="absolute top-1 left-1">
                                    <FontAwesomeIcon
                                      icon={oddsChangeInfo.icon}
                                      className={`${oddsChangeInfo.color} text-xs`}
                                      title={`${
                                        oddsChangeInfo.isIncrease
                                          ? "Increased"
                                          : "Decreased"
                                      } by ${oddsChangeInfo.percentage}%`}
                                    />
                                  </div>
                                )}

                                <span
                                  className={`${
                                    odd.highlight
                                      ? "flex items-center gap-[1px]"
                                      : ""
                                  } ${getLabelStyling(
                                    odd,
                                    isSelected,
                                    disabled
                                  )}`}
                                >
                                  {odd.label}
                                  {odd.highlight && (
                                    <span className="ml-[2px] mb-[1px] w-0 h-0 border-b-[9px] border-b-[#11A306] border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent"></span>
                                  )}
                                </span>

                                <div className="flex flex-col items-center">
                                  <span
                                    className={getValueStyling(
                                      odd,
                                      isSelected,
                                      disabled
                                    )}
                                  >
                                    {odd.value}
                                  </span>
                                  {/* Show original value if odds changed */}
                                  {oddsChangeInfo && (
                                    <span
                                      className={`text-xs ${oddsChangeInfo.color} line-through opacity-75`}
                                    >
                                      {odd.originalValue}
                                    </span>
                                  )}
                                </div>

                                {/* Pulse animation for newly selected items */}
                                {isSelected && (
                                  <div className="absolute inset-0 rounded-[6px] animate-pulse bg-yellow-400 opacity-30 pointer-events-none"></div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    // Empty placeholder to maintain column structure
                    <div className="flex gap-[4px] h-16 max-sm:h-12">
                      <div className="w-3/4 sm:w-1/2 opacity-50">
                        <div className="w-full h-full rounded-[6px]  flex items-center justify-center">
                          <span className="text-xs text-gray-400"></span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
};

export default MatchRow;
